import { Link } from 'react-router-dom';

interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  categoryName: string;
  coverImage: string;
  author: string;
  publishDate: string;
  readTime: number;
  viewCount: number;
  tags: string[];
  isHot: boolean;
  isFeatured: boolean;
}

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative overflow-hidden rounded-lg bg-background-100 border border-background-200/50 hover:border-primary-500/30 transition-all duration-300"
      >
        <div className="relative h-64 md:h-72 overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            title={`${article.title} - 黑料在线看`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/20 to-transparent"></div>
          {article.isHot && (
            <span className="absolute top-3 left-3 bg-primary-500 text-background-50 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
              HOT
            </span>
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <span className="inline-block text-xs text-accent-400 bg-accent-500/10 px-2 py-0.5 rounded-full mb-2 whitespace-nowrap">
            {article.categoryName}
          </span>
          <h3 className="font-heading text-lg md:text-xl text-foreground-50 group-hover:text-primary-400 transition-colors line-clamp-2 mb-2">
            {article.title}
          </h3>
          <p className="text-foreground-400 text-sm line-clamp-2 mb-3">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line"></i>
              </div>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-eye-line"></i>
              </div>
              {article.viewCount >= 10000
                ? `${(article.viewCount / 10000).toFixed(1)}万`
                : article.viewCount}
            </span>
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-open-line"></i>
              </div>
              {article.readTime}分钟阅读
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 p-3 rounded-lg bg-background-100 hover:bg-background-200/50 border border-transparent hover:border-background-200/50 transition-all duration-300"
      >
        <div className="w-24 h-24 md:w-28 md:h-28 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            title={`${article.title} - 黑料在线看`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-sm md:text-base text-foreground-100 group-hover:text-primary-400 transition-colors line-clamp-2 mb-1.5">
            {article.title}
          </h3>
          <p className="text-foreground-500 text-xs line-clamp-2 mb-2">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span>{article.publishDate}</span>
            <span>{article.readTime}分钟阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block bg-background-100 rounded-lg border border-background-200/50 hover:border-primary-500/30 overflow-hidden transition-all duration-300"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          title={`${article.title} - 黑料在线看`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {article.isHot && (
          <span className="absolute top-3 left-3 bg-primary-500 text-background-50 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
            HOT
          </span>
        )}
      </div>
      <div className="p-4">
        <span className="inline-block text-xs text-accent-400 bg-accent-500/10 px-2 py-0.5 rounded-full mb-2 whitespace-nowrap">
          {article.categoryName}
        </span>
        <h3 className="font-heading text-base text-foreground-100 group-hover:text-primary-400 transition-colors line-clamp-2 mb-2">
          {article.title}
        </h3>
        <p className="text-foreground-500 text-sm line-clamp-2 mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line"></i>
            </div>
            {article.publishDate}
          </span>
          <span className="flex items-center gap-1">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-eye-line"></i>
            </div>
            {article.viewCount >= 10000
              ? `${(article.viewCount / 10000).toFixed(1)}万`
              : article.viewCount}
          </span>
        </div>
      </div>
    </Link>
  );
}